import type { Editor } from '@tiptap/core';
import { getNodeType, isAtStartOfNode, isNodeActive } from '@tiptap/core';
import type { Node, NodeType } from '@tiptap/pm/model';

import { EditorState } from '@tiptap/pm/state';

export const handleBackspace = (editor: Editor, name: string, parentListTypes: string[]) => {
  if (editor.commands.undoInputRule()) {
    return true;
  }
  if (editor.state.selection.from !== editor.state.selection.to) {
    return false;
  }

  if (!isNodeActive(editor.state, name) && hasListBefore(editor.state, name, parentListTypes)) {
    const { $anchor } = editor.state.selection;

    const $listPos = editor.state.doc.resolve($anchor.before() - 1);

    const listDescendants: Array<{ node: Node; pos: number }> = [];

    $listPos.node().descendants((node, pos) => {
      if (node.type.name === name) {
        listDescendants.push({ node, pos });
      }
    });

    const lastItem = listDescendants.at(-1);

    if (!lastItem) {
      return false;
    }

    const $lastItemPos = editor.state.doc.resolve($listPos.start() + lastItem.pos + 1);

    return editor
      .chain()
      .cut({ from: $anchor.start() - 1, to: $anchor.end() + 1 }, $lastItemPos.end())
      .joinForward()
      .run();
  }

  if (!isNodeActive(editor.state, name)) {
    return false;
  }

  if (!isAtStartOfNode(editor.state)) {
    return false;
  }

  const listItemPos = findListItemPos(name, editor.state);

  if (!listItemPos) {
    return false;
  }

  const $prev = editor.state.doc.resolve(listItemPos.$pos.pos - 2);
  const prevNode = $prev.node(listItemPos.depth);

  const previousListItemHasSubList = listItemHasSubList(name, editor.state, prevNode);
  if (hasListItemBefore(name, editor.state) && !previousListItemHasSubList) {
    return editor.commands.joinItemBackward();
  }

  return editor.chain().liftListItem(name).run();
};

export const hasListBefore = (
  editorState: EditorState,
  name: string,
  parentListTypes: string[],
) => {
  if (!editorState?.selection) return false;

  const { $anchor } = editorState.selection;
  if (!$anchor || typeof $anchor.pos !== 'number') return false;

  if (!editorState.doc || typeof editorState.doc.resolve !== 'function') return false;

  const previousNodePos = Math.max(0, $anchor.pos - 2);
  const resolved = editorState.doc.resolve(previousNodePos);
  const previousNode = resolved?.node?.();

  if (!previousNode?.type?.name) return false;
  return parentListTypes.includes(previousNode.type.name);
};

export const findListItemPos = (typeOrName: string | NodeType, state: EditorState) => {
  if (!state?.selection) return null;

  const { $from } = state.selection;
  if (!$from || typeof $from.depth !== 'number') return null;

  const nodeType = getNodeType(typeOrName, state.schema);
  if (!nodeType) return null;

  let currentDepth = $from.depth;
  let currentPos = $from.pos;
  let targetDepth: number | null = null;

  while (currentDepth > 0 && targetDepth === null) {
    const currentNode = $from.node?.(currentDepth);
    if (currentNode?.type === nodeType) {
      targetDepth = currentDepth;
    } else {
      currentDepth -= 1;
      currentPos -= 1;
    }
  }

  if (targetDepth === null) return null;
  if (!state.doc?.resolve) return null;

  return { $pos: state.doc.resolve(currentPos), depth: targetDepth };
};

export const hasListItemBefore = (typeOrName: string, state: EditorState): boolean => {
  const { $anchor } = state.selection;

  const $targetPos = state.doc.resolve($anchor.pos - 2);

  if ($targetPos.index() === 0) {
    return false;
  }

  if ($targetPos.nodeBefore?.type.name !== typeOrName) {
    return false;
  }

  return true;
};

export const listItemHasSubList = (typeOrName: string, state: EditorState, node?: Node) => {
  if (!node) {
    return false;
  }

  const nodeType = getNodeType(typeOrName, state.schema);

  let hasSubList = false;

  node.descendants((child) => {
    if (child.type === nodeType) {
      hasSubList = true;
    }
  });

  return hasSubList;
};
